// Global Category Switcher
class CategorySwitcher {
  constructor() {
    this.currentCategory = null;
    this.isTransitioning = false;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Set initial state from localStorage
    const savedCategory = localStorage.getItem('selectedCategory') || 'ethnic';
    this.switchCategory(savedCategory, false);

    // Add event listeners for menu buttons
    document.addEventListener('click', (e) => {
      const menuBtn = e.target.closest('.menu-btn');
      if (menuBtn && menuBtn.dataset.category) {
        e.preventDefault();
        this.switchCategory(menuBtn.dataset.category, true);
      }
    });
  }

  switchCategory(category, animate = true) {
    if (category === this.currentCategory) return;
    if (this.isTransitioning && animate) return;

    this.isTransitioning = true;
    const previousCategory = this.currentCategory;

    // Save to localStorage
    localStorage.setItem('selectedCategory', category);

    if (animate) {
      this._createOrShowOverlay();
      
      // Wait slightly longer for overlay to fade in completely (350ms)
      setTimeout(() => {
        // Yield to the browser to ensure the overlay paint is finished
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Swap categories (this triggers a heavy layout recalculation)
            this._applyCategory(category);
            this.refreshSwipers();

            // Keep it visible for a moment so the user sees the logo
            setTimeout(() => {
              this._hideOverlay();
              this.isTransitioning = false;
            }, 600);
          });
        });
      }, 350);

    } else {
      // Instant swap — no animation (initial load)
      this._applyCategory(category);
      this.isTransitioning = false;
      setTimeout(() => this.refreshSwipers(), 50);
    }

    this.currentCategory = category;
    this.updateButtonStates(category);
    this.dispatchCategoryChange(category, previousCategory);
  }

  _createOrShowOverlay() {
    let overlay = document.getElementById('category-switcher-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'category-switcher-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: #ffffff;
        z-index: 9999999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      `;

      // Try to find the logo from the header
      const logoImg = document.querySelector('.header__logo img, .regular-logo img, .mobile-logo-image img');
      if (logoImg) {
        const clonedLogo = logoImg.cloneNode(true);
        clonedLogo.className = ''; // Strip theme-specific positioning classes
        clonedLogo.style.cssText = `
          position: static !important;
          max-width: 250px;
          height: auto;
          object-fit: contain;
          animation: categoryLogoPulse 1.5s infinite ease-in-out;
        `;
        // Add pulse animation
        const style = document.createElement('style');
        style.innerHTML = `
          @keyframes categoryLogoPulse {
            0% { transform: scale(0.95); opacity: 0.8; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(0.95); opacity: 0.8; }
          }
        `;
        document.head.appendChild(style);
        overlay.appendChild(clonedLogo);
      } else {
        // Fallback text if logo not found
        const text = document.createElement('h2');
        text.innerText = 'Loading...';
        overlay.appendChild(text);
      }
      
      document.body.appendChild(overlay);
    }
    
    // Show overlay
    // Force reflow
    void overlay.offsetHeight;
    overlay.style.visibility = 'visible';
    overlay.style.opacity = '1';
    
    return overlay;
  }

  _hideOverlay() {
    const overlay = document.getElementById('category-switcher-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.style.visibility = 'hidden';
      }, 300);
    }
  }

  _applyCategory(category) {
    document.body.classList.remove('show-ethnic', 'show-lifestyle');
    document.body.classList.add('show-' + category);
    document.body.setAttribute('data-category', category);
  }

  updateButtonStates(category) {
    document.querySelectorAll('.menu-btn').forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-category') === category);
    });
  }

  refreshSwipers() {
    // Trigger resize so Swiper can recalculate widths  
    window.dispatchEvent(new Event('resize'));

    // Update all .slideshow swipers
    document.querySelectorAll('.slideshow').forEach(slideshow => {
      if (slideshow.swiperInstance) {
        slideshow.swiperInstance.update();
      }
    });

    // Update all other Swiper instances
    document.querySelectorAll('.swiper').forEach(el => {
      if (el.swiper) {
        el.swiper.update();
      }
    });
  }

  dispatchCategoryChange(category, previousCategory) {
    document.dispatchEvent(new CustomEvent('categoryChanged', {
      detail: { category, previousCategory }
    }));
  }
}

// Initialize globally
window.categorySwitcher = new CategorySwitcher();

// Re-init when Shopify theme editor loads new sections
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      if (window.categorySwitcher) {
        window.categorySwitcher.refreshSwipers();
        window.categorySwitcher.updateButtonStates(
          window.categorySwitcher.currentCategory || 'ethnic'
        );
      }
    }, 300);
  });
}